import type { ReactNode } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { useOperationVerificationStatus } from '@/hooks/useOperationVerificationStatus'
import { toOperationVerificationSummaryViewModel } from '@/lib/venue/operationVerificationStatusViewModel'
import type { OperationVerificationView } from '@/lib/venue/operationVerificationTypes'

/**
 * 운영 인증 요약 카드 — 내 구장 관리 홈·구장 정보 화면 공용(`#778` 요구 1·2).
 *
 * 🚨 문구는 서버가 내려주는 상태·기한·사유로 구성한다(`OperationVerificationView` 그대로, 클라이언트
 * 재계산 없음) — `operationVerificationStatusViewModel.ts`가 매핑을 소유한다.
 */
export function OperationVerificationSummaryCard({ venueId }: { venueId: string }) {
  const navigate = useNavigate()
  const statusQuery = useOperationVerificationStatus(venueId)

  return (
    <Card>
      <CardHeader
        title="운영 인증"
        action={
          <div className="flex gap-2">
            {/* 🚨 #1191 — 인증 상태와 무관하게 항상 노출한다(백엔드 D7 "M1~M4 전부 게이팅 없음" —
                인증 유효·만료·미완료 어느 상태에서도 사업자 정보 수정 요청이 가능하다). */}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigate(`/venues/${venueId}/business-info`)
              }}
            >
              사업자 정보 수정
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigate(`/venues/${venueId}/operation-verification/status`)
              }}
            >
              자세히 보기
            </Button>
          </div>
        }
      />
      <CardBody className="space-y-3">
        {statusQuery.isLoading ? <LoadingState rows={2} label="운영 인증 상태를 불러오는 중" /> : null}
        {statusQuery.isError ? (
          <ErrorState
            error={statusQuery.error}
            onRetry={() => {
              void statusQuery.refetch()
            }}
          />
        ) : null}
        {statusQuery.data ? <SummaryFacts venueId={venueId} view={statusQuery.data} navigate={navigate} /> : null}
      </CardBody>
    </Card>
  )
}

function SummaryFacts({
  venueId,
  view,
  navigate,
}: {
  venueId: string
  view: OperationVerificationView
  navigate: NavigateFunction
}) {
  const vm = toOperationVerificationSummaryViewModel(view)

  return (
    <>
      {vm.needsAction ? (
        <Alert
          tone="danger"
          title="운영 인증이 필요합니다"
          action={
            <Button
              size="sm"
              onClick={() => {
                navigate(`/venues/${venueId}/operation-verification`)
              }}
            >
              인증 진행하기
            </Button>
          }
        >
          기한 안에 인증하지 않으면 구장이 목록·검색에서 제외되고 신규 온라인 예약을 받을 수 없어요.
        </Alert>
      ) : null}

      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryFact label="인증 상태" value={<Badge tone={vm.tone}>{vm.badgeLabel}</Badge>} />
        <SummaryFact label="인증 기한" value={vm.verifiedUntilText} />
        <SummaryFact label="최종 확인 시점" value={vm.verifiedAtText} />
        <SummaryFact label="구장 노출 상태" value={vm.exposureText} />
      </dl>
    </>
  )
}

function SummaryFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-slateish-500">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink-700">{value}</dd>
    </div>
  )
}
